/**
 * Publish Store — manages publish task state for the publish center UI.
 *
 * Tracks both single-document and site-level publish tasks.
 * Actions call the Engine publish/* RPCs via the platform service.
 * Publish lifecycle events are forwarded to the log panel for visibility.
 */

import type {
  PublishCompletedEvent,
  PublishFailedEvent,
  PublishProgressEvent,
  PublishResult,
  PublishSiteCompletedEvent,
  PublishSiteFailedEvent,
  PublishSiteProgressEvent,
  PublishSiteResult,
  PublishSiteStartParams,
  PublishStartedEvent,
  PublishStartParams,
} from "@magic-md/engine-client/protocol";
import { t as translate, type LocaleParams } from "@magic-md/i18n";
import { create } from "zustand";
import {
  createDocumentPublishConfigFile,
  createDocumentPublishReportFile,
  createDocumentPublishStatusFile,
  createFolderPublishConfigFile,
  createFolderPublishReportFile,
  createFolderPublishStatusFile,
  getCurrentPublishPresentationSnapshot,
  getBundleDirFromPublishOutputPath,
  getCurrentPublishPresentation,
  getDocumentBundleDir,
  getDocumentPublishConfigPath,
  getDocumentPublishDir,
  getDocumentPublishReportPath,
  getDocumentPublishStatusPath,
  getFolderPublishDir,
  getFolderPublishConfigPath,
  getFolderPublishReportPath,
  getFolderPublishStatusPath,
  getPathDirname,
  getPublishDirFromOutputPath,
  normalizeSingleDocPublishOutDir,
  resolveFolderPublishOutDir,
} from "../lib/publish-utils";
import { getPlatformService } from "../platform";
import { useEditorStore } from "./editor-store";
import { useLogStore } from "./log-store";
import { getNotificationBus } from "./notification-bus";
import { usePluginExecutionStore } from "./plugin-execution-store";

// ── State ──

export interface PublishTaskState {
  taskId: string;
  sourceFilePath?: string;
  status: "running" | "completed" | "failed" | "cancelled";
  startedAt?: string;
  progress?: { message: string; percent: number };
  result?: PublishResult;
  error?: string;
}

export interface SitePublishTaskState {
  taskId: string;
  status: "running" | "completed" | "failed" | "cancelled";
  progress?: { message: string; percent: number };
  result?: PublishSiteResult;
  error?: string;
  /** Current page being processed */
  currentPage?: string;
  pagesProcessed?: number;
  pagesTotal?: number;
}

export interface SiteServerState {
  starting: boolean;
  running: boolean;
  url: string;
  port: number;
  host: string;
  outputDir?: string;
}

export interface DocumentPublishRunOptions {
  outDir?: string;
  clean?: boolean;
  rerunAll?: boolean;
  useOpenBufferContent?: boolean;
}

export interface FolderPublishRunOptions {
  outDir?: string;
  clean?: boolean;
  rerunAll?: boolean;
  incremental?: boolean;
  siteTitle?: string;
}

interface ResolvedFolderPublishRunOptions {
  outDir: string;
  clean: boolean;
  rerunAll: boolean;
  incremental: boolean;
  siteTitle: string;
}

interface ResolvedDocumentPublishRunOptions {
  outDir: string;
  clean: boolean;
  rerunAll: boolean;
  useOpenBufferContent: boolean;
}

interface PublishState {
  /** Single-document publish task */
  currentTask: PublishTaskState | null;
  /** File-scoped publish tasks shown in Explorer */
  fileTasks: Record<string, PublishTaskState>;
  /** taskId -> source file path */
  taskFileIndex: Record<string, string>;
  /** Site-level (multi-document) publish task */
  siteTask: SitePublishTaskState | null;
  /** Folder-level publish tasks (key = folderPath) */
  folderTasks: Record<string, SitePublishTaskState>;
  /** taskId -> folder path for folder-level publish */
  taskFolderIndex: Record<string, string>;
  history: PublishResult[];
  /** Site preview server state */
  siteServer: SiteServerState | null;
}

const PUBLISH_START_PENDING_STALE_MS = 30_000;
const DOCUMENT_PUBLISH_POLL_INTERVAL_MS = 1_000;
const DOCUMENT_PUBLISH_EVENT_WATCHDOG_MS = 5_000;
const documentPublishPollers = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

export function isStalePendingPublishTask(
  task: PublishTaskState | null | undefined,
  now = Date.now(),
): boolean {
  if (!task || task.status !== "running" || task.taskId) return false;
  if (!task.startedAt) return true;
  const startedAtMs = Date.parse(task.startedAt);
  return !Number.isFinite(startedAtMs)
    ? true
    : now - startedAtMs > PUBLISH_START_PENDING_STALE_MS;
}

export function isActiveRunningPublishTask(
  task: PublishTaskState | null | undefined,
): boolean {
  return !!task && task.status === "running" && !isStalePendingPublishTask(task);
}

function clearDocumentPublishPoller(taskId: string): void {
  const timer = documentPublishPollers.get(taskId);
  if (timer) {
    clearTimeout(timer);
    documentPublishPollers.delete(taskId);
  }
}

function unrefTimer(timer: ReturnType<typeof setTimeout>): void {
  const maybeNodeTimer = timer as { unref?: () => void };
  maybeNodeTimer.unref?.();
}

function hasRunningDocumentPublishTask(taskId: string): boolean {
  const state = usePublishStore.getState();
  if (state.currentTask?.status === "running" && state.currentTask.taskId === taskId) {
    return true;
  }
  return Object.values(state.fileTasks).some(
    (task) => task.status === "running" && task.taskId === taskId,
  );
}

function documentPublishPollDelay(): number {
  return getNotificationBus().connected
    ? DOCUMENT_PUBLISH_EVENT_WATCHDOG_MS
    : DOCUMENT_PUBLISH_POLL_INTERVAL_MS;
}

function scheduleDocumentPublishPolling(taskId: string): void {
  if (!taskId || documentPublishPollers.has(taskId)) return;

  const poll = async () => {
    try {
      if (!hasRunningDocumentPublishTask(taskId)) {
        clearDocumentPublishPoller(taskId);
        return;
      }

      const state = await getPlatformService().engine.publishGetState({
        taskId,
      });
      const publishStore = usePublishStore.getState();
      if (state.status === "completed" && state.result) {
        clearDocumentPublishPoller(taskId);
        publishStore.handleCompleted({ taskId: state.taskId, result: state.result });
        return;
      }
      if (state.status === "failed") {
        clearDocumentPublishPoller(taskId);
        publishStore.handleFailed({
          taskId: state.taskId,
          error: state.error ?? "unknown error",
        });
        return;
      }
      if (state.status === "cancelled") {
        clearDocumentPublishPoller(taskId);
        return;
      }
      if (state.progress) {
        publishStore.handleProgress({
          taskId: state.taskId,
          phase: "writing",
          message: state.progress.message,
          percent: state.progress.percent,
        });
      }
    } catch (err) {
      console.warn("[publish-store] publish state polling failed:", err);
    }

    if (hasRunningDocumentPublishTask(taskId)) {
      const timer = setTimeout(poll, documentPublishPollDelay());
      unrefTimer(timer);
      documentPublishPollers.set(taskId, timer);
    } else {
      clearDocumentPublishPoller(taskId);
    }
  };

  const timer = setTimeout(poll, documentPublishPollDelay());
  unrefTimer(timer);
  documentPublishPollers.set(taskId, timer);
}

interface PublishActions {
  // Single-document
  startPublish: (
    params: PublishStartParams,
    options?: { sourceFilePath?: string },
  ) => Promise<boolean>;
  publishFile: (
    filePath: string,
    options?: DocumentPublishRunOptions,
  ) => Promise<boolean>;
  saveDocumentPublishConfig: (
    filePath: string,
    options: DocumentPublishRunOptions,
  ) => Promise<void>;
  cancelPublish: () => Promise<void>;
  handleStarted: (event: PublishStartedEvent) => void;
  handleProgress: (event: PublishProgressEvent) => void;
  handleCompleted: (event: PublishCompletedEvent) => void;
  handleFailed: (event: PublishFailedEvent) => void;
  clearFileTask: (filePath: string) => void;
  clearTask: () => void;

  // Site-level
  startSitePublish: (params: PublishSiteStartParams) => Promise<void>;
  cancelSitePublish: () => Promise<void>;
  handleSiteProgress: (event: PublishSiteProgressEvent) => void;
  handleSiteCompleted: (event: PublishSiteCompletedEvent) => void;
  handleSiteFailed: (event: PublishSiteFailedEvent) => void;
  clearSiteTask: () => void;

  // Folder-level publish
  publishFolder: (
    folderPath: string,
    options?: FolderPublishRunOptions,
  ) => Promise<void>;
  saveFolderPublishConfig: (
    folderPath: string,
    options: FolderPublishRunOptions,
  ) => Promise<void>;
  cancelFolderPublish: (folderPath: string) => Promise<void>;
  clearFolderTask: (folderPath: string) => void;

  // Site server
  startSiteServer: (outputDir: string, port?: number) => Promise<void>;
  stopSiteServer: () => Promise<void>;
}

type PublishStore = PublishState & PublishActions;

function tr(key: string, params?: LocaleParams): string {
  return translate("editor", key, params);
}

/** Helper: log to the editor log panel */
function publishLog(level: "info" | "warn" | "error", message: string) {
  useLogStore.getState().log(level, `[${tr("publish.logPrefix")}] ${message}`, { source: "engine" });
}

function notifyBundleRefresh(detail: {
  filePath?: string;
  bundleDir?: string;
  publishDir?: string;
}): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("magic-md:bundle-refresh", { detail }),
  );
}

async function refreshDocumentPublishTree(filePath: string): Promise<void> {
  const editor = useEditorStore.getState();
  const bundleDir = getDocumentBundleDir(filePath);
  const publishDir = getDocumentPublishDir(filePath);

  await editor.refreshSubtree(bundleDir).catch(() => {});
  if (publishDir) {
    await editor.refreshSubtree(publishDir).catch(() => {});
  }
  notifyBundleRefresh({ filePath, bundleDir, publishDir });
}

async function refreshPublishedOutput(outputPath: string): Promise<void> {
  const editor = useEditorStore.getState();
  const bundleDir = getBundleDirFromPublishOutputPath(outputPath);
  const publishDir = getPublishDirFromOutputPath(outputPath);

  if (bundleDir) {
    await editor.refreshSubtree(bundleDir).catch(() => {});
  }

  const finalTarget = publishDir ?? getPathDirname(outputPath);
  if (finalTarget) {
    await editor.refreshSubtree(finalTarget).catch(() => {});
  }
  notifyBundleRefresh({
    bundleDir: bundleDir ?? undefined,
    publishDir: publishDir ?? finalTarget,
  });
}

function getActiveEditorFilePath(): string | undefined {
  const editor = useEditorStore.getState();
  return editor.tabs.find((tab) => tab.id === editor.activeTabId)?.filePath;
}

function createRunningTask(sourceFilePath?: string): PublishTaskState {
  return {
    taskId: "",
    sourceFilePath,
    status: "running",
    startedAt: new Date().toISOString(),
    progress: { message: tr("publish.progress.startingDocument"), percent: 0 },
  };
}

function normalizeDocumentPublishOptions(
  filePath: string,
  options: DocumentPublishRunOptions | undefined,
): ResolvedDocumentPublishRunOptions {
  const outDir = normalizeSingleDocPublishOutDir(filePath, options?.outDir);
  if (!outDir) {
    throw new Error(tr("publish.error.defaultDocumentOutDir"));
  }
  return {
    outDir,
    clean: !!options?.clean,
    rerunAll: !!options?.rerunAll,
    useOpenBufferContent: options?.useOpenBufferContent !== false,
  };
}

async function writeDocumentPublishJson(
  path: string,
  value: unknown,
): Promise<void> {
  await getPlatformService().engine.workspaceWriteFile({
    path,
    content: `${JSON.stringify(value, null, 2)}\n`,
  });
}

async function persistDocumentPublishConfigFile(
  filePath: string,
  options: DocumentPublishRunOptions,
): Promise<void> {
  const resolved = normalizeDocumentPublishOptions(filePath, options);
  const configFile = createDocumentPublishConfigFile({
    filePath,
    settings: {
      outDir: resolved.outDir,
      clean: resolved.clean,
      rerunAll: resolved.rerunAll,
      useOpenBufferContent: resolved.useOpenBufferContent,
    },
    presentation: getCurrentPublishPresentationSnapshot(),
  });
  await writeDocumentPublishJson(
    getDocumentPublishConfigPath(filePath),
    configFile,
  );
  await refreshDocumentPublishTree(filePath);
}

async function persistDocumentPublishState(
  task: PublishTaskState,
  options?: { refreshTree?: boolean; includeReport?: boolean },
): Promise<void> {
  if (!task.sourceFilePath) return;

  await writeDocumentPublishJson(
    getDocumentPublishStatusPath(task.sourceFilePath),
    createDocumentPublishStatusFile({
      filePath: task.sourceFilePath,
      taskId: task.taskId,
      status: task.status,
      progress: task.progress,
      outputPath: task.result?.outputPath,
      generatedAt: task.result?.generatedAt,
      error: task.error,
    }),
  );

  if (options?.includeReport) {
    await writeDocumentPublishJson(
      getDocumentPublishReportPath(task.sourceFilePath),
      createDocumentPublishReportFile({
        filePath: task.sourceFilePath,
        taskId: task.taskId,
        status: task.status,
        result: task.result,
        error: task.error,
      }),
    );
  }

  if (options?.refreshTree) {
    await refreshDocumentPublishTree(task.sourceFilePath);
  }
}

function normalizeFolderPublishOptions(
  folderPath: string,
  options: FolderPublishRunOptions | undefined,
): ResolvedFolderPublishRunOptions {
  const defaultOutDir = resolveFolderPublishOutDir(folderPath);
  const outDir = options?.outDir?.trim() || defaultOutDir;
  if (!outDir) {
    throw new Error(tr("publish.error.defaultFolderOutDir"));
  }
  return {
    outDir,
    clean: !!options?.clean,
    rerunAll: !!options?.rerunAll,
    incremental: options?.incremental !== false,
    siteTitle: options?.siteTitle?.trim() || folderPath.split("/").pop() || folderPath,
  };
}

async function persistFolderPublishConfigFile(
  folderPath: string,
  options: FolderPublishRunOptions,
): Promise<void> {
  const resolved = normalizeFolderPublishOptions(folderPath, options);
  const configFile = createFolderPublishConfigFile({
    folderPath,
    settings: {
      outDir: resolved.outDir,
      clean: resolved.clean,
      rerunAll: resolved.rerunAll,
      incremental: resolved.incremental,
      siteTitle: resolved.siteTitle,
    },
  });
  await writeDocumentPublishJson(
    getFolderPublishConfigPath(folderPath),
    configFile,
  );
}

async function persistFolderPublishState(
  folderPath: string,
  task: SitePublishTaskState,
  options?: { refreshTree?: boolean; includeReport?: boolean },
): Promise<void> {
  await writeDocumentPublishJson(
    getFolderPublishStatusPath(folderPath),
    createFolderPublishStatusFile({
      folderPath,
      taskId: task.taskId,
      status: task.status,
      progress: task.progress,
      outputPath: task.result?.outputDir,
      generatedAt: task.result?.generatedAt,
      error: task.error,
    }),
  );

  if (options?.includeReport) {
    await writeDocumentPublishJson(
      getFolderPublishReportPath(folderPath),
      createFolderPublishReportFile({
        folderPath,
        taskId: task.taskId,
        status: task.status,
        error: task.error,
      }),
    );
  }

  if (options?.refreshTree) {
    const editor = useEditorStore.getState();
    await editor.refreshSubtree(folderPath).catch(() => {});
    await editor.refreshSubtree(getFolderPublishDir(folderPath)).catch(() => {});
  }
}

// ── Store ──

export const usePublishStore = create<PublishStore>((set, get) => ({
  currentTask: null,
  fileTasks: {},
  taskFileIndex: {},
  siteTask: null,
  folderTasks: {},
  taskFolderIndex: {},
  history: [],
  siteServer: null,

  // ── Single-document actions ──

  startPublish: async (params, options) => {
    const sourceFilePath =
      options?.sourceFilePath ??
      ("filePath" in params && params.filePath
        ? params.filePath
        : getActiveEditorFilePath());
    const runningTask = createRunningTask(sourceFilePath);

    // Start notification setup in the background. Publish itself must not block
    // on Engine initialization because a stale notification init promise can
    // otherwise prevent publish/start from being sent at all.
    void usePluginExecutionStore.getState()._initEngine();

    // Set running state BEFORE the RPC so notifications arriving before the
    // response (or during a WS reconnect race) are not silently dropped.
    set((state) => ({
      currentTask: runningTask,
      fileTasks: sourceFilePath
        ? { ...state.fileTasks, [sourceFilePath]: runningTask }
        : state.fileTasks,
    }));
    void persistDocumentPublishState(runningTask, {
      refreshTree: !!sourceFilePath,
      includeReport: !!sourceFilePath,
    });

    const engine = getPlatformService().engine;
    try {
      const result = await engine.publishStart(params);
      let startedTask: PublishTaskState | null = null;
      // Update with the real taskId from the server
      set((state) => {
        const currentTask =
          state.currentTask &&
          (state.currentTask.taskId === result.taskId ||
            (!state.currentTask.taskId &&
              state.currentTask.sourceFilePath === sourceFilePath))
            ? { ...state.currentTask, taskId: result.taskId }
            : state.currentTask;
        if (currentTask?.sourceFilePath === sourceFilePath) {
          startedTask = currentTask;
        }
        const fileTasks = sourceFilePath
          ? {
              ...state.fileTasks,
              [sourceFilePath]: {
                ...(state.fileTasks[sourceFilePath] ?? runningTask),
                taskId: result.taskId,
                sourceFilePath,
              },
            }
          : state.fileTasks;
        if (sourceFilePath) {
          startedTask = fileTasks[sourceFilePath] ?? startedTask;
        }
        const taskFileIndex = sourceFilePath
          ? { ...state.taskFileIndex, [result.taskId]: sourceFilePath }
          : state.taskFileIndex;
        return { currentTask, fileTasks, taskFileIndex };
      });
      scheduleDocumentPublishPolling(result.taskId);
      if (startedTask) {
        void persistDocumentPublishState(startedTask, {
          refreshTree: !!sourceFilePath,
          includeReport: !!sourceFilePath,
        });
      }
      return true;
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      const failedTask: PublishTaskState = {
        ...runningTask,
        status: "failed",
        error,
      };
      set((state) => ({
        currentTask:
          state.currentTask &&
          !state.currentTask.taskId &&
          state.currentTask.sourceFilePath === sourceFilePath
            ? failedTask
            : state.currentTask,
        fileTasks: sourceFilePath
          ? { ...state.fileTasks, [sourceFilePath]: failedTask }
          : state.fileTasks,
      }));
      void persistDocumentPublishState(failedTask, {
        refreshTree: !!sourceFilePath,
        includeReport: !!sourceFilePath,
      });
      return false;
    }
  },

  publishFile: async (filePath, options) => {
    const editor = useEditorStore.getState();
    const existingTask = get().fileTasks[filePath];
    if (existingTask?.status === "running") {
      if (isStalePendingPublishTask(existingTask)) {
        set((state) => {
          const failedTask: PublishTaskState = {
            ...existingTask,
            status: "failed",
            error: tr("publish.error.previousStartStale"),
            progress: { message: tr("publish.progress.pendingExpired"), percent: 0 },
          };
          return {
            currentTask:
              state.currentTask?.sourceFilePath === filePath
                ? failedTask
                : state.currentTask,
            fileTasks: { ...state.fileTasks, [filePath]: failedTask },
          };
        });
      } else {
        console.warn("[publish-store] Document already publishing:", filePath);
        editor.setStatusMessage(tr("publish.status.documentRunning"));
        return false;
      }
    }

    try {
      console.log("[publish-store] Starting publishFile for:", filePath);
      const resolvedOptions = normalizeDocumentPublishOptions(
        filePath,
        options,
      );
      const tab = editor.tabs.find((entry) => entry.filePath === filePath);
      const { presentationProfileId, presentationStyleVariables } =
        getCurrentPublishPresentation();

      await get().saveDocumentPublishConfig(filePath, resolvedOptions);
      const started = await get().startPublish(
        {
          filePath,
          content: resolvedOptions.useOpenBufferContent
            ? tab?.content
            : undefined,
          outDir: resolvedOptions.outDir,
          clean: resolvedOptions.clean,
          rerunAll: resolvedOptions.rerunAll,
          presentationProfileId,
          presentationStyleVariables,
        },
        { sourceFilePath: filePath },
      );
      console.log("[publish-store] Publish started result:", started);
      editor.setStatusMessage(
        started
          ? tr("publish.status.started")
          : tr("publish.status.startFailedCheckStatus"),
      );
      return started;
    } catch (err) {
      console.error("[publish-store] publishFile error:", err);
      const errMsg = err instanceof Error ? err.message : String(err);
      const failedTask: PublishTaskState = {
        taskId: "",
        sourceFilePath: filePath,
        status: "failed",
        error: errMsg,
      };
      editor.setStatusMessage(tr("publish.status.failed", { message: errMsg.slice(0, 80) }));
      set((state) => ({
        currentTask: failedTask,
        fileTasks: { ...state.fileTasks, [filePath]: failedTask },
      }));
      void persistDocumentPublishState(failedTask, {
        refreshTree: true,
        includeReport: true,
      });
      return false;
    }
  },

  saveDocumentPublishConfig: async (filePath, options) => {
    await persistDocumentPublishConfigFile(filePath, options);
  },

  cancelPublish: async () => {
    const { currentTask } = get();
    if (!currentTask || currentTask.status !== "running") return;

    const taskId = currentTask.taskId;
    clearDocumentPublishPoller(taskId);
    const engine = getPlatformService().engine;
    if (taskId) {
      await engine.publishCancel({ taskId });
    }
    // Re-read state after the await — startPublish may have updated it
    const cancelledTasks: PublishTaskState[] = [];
    set((state) => {
      const task = state.currentTask;
      if (!task || task.status !== "running") return {};
      const filePath =
        task.sourceFilePath ??
        (task.taskId ? state.taskFileIndex[task.taskId] : undefined);
      const nextTask: PublishTaskState = {
        ...task,
        status: "cancelled",
        progress: { message: tr("publish.progress.cancelled"), percent: 0 },
      };
      cancelledTasks.push(nextTask);
      return {
        currentTask: nextTask,
        fileTasks: filePath
          ? { ...state.fileTasks, [filePath]: nextTask }
        : state.fileTasks,
      };
    });
    const cancelledTask = cancelledTasks[0];
    if (cancelledTask) {
      void persistDocumentPublishState(cancelledTask, {
        refreshTree: !!cancelledTask.sourceFilePath,
        includeReport: !!cancelledTask.sourceFilePath,
      });
    }
  },

  handleStarted: (event) => {
    let nextTask: PublishTaskState | null = null;
    set((state) => {
      if (!event.filePath && !event.sessionId) {
        return {};
      }
      const filePath = event.filePath;
      const currentTask =
        state.currentTask &&
        state.currentTask.status === "running" &&
        (state.currentTask.taskId === event.taskId ||
          (!state.currentTask.taskId &&
            (!filePath || state.currentTask.sourceFilePath === filePath)))
          ? {
              ...state.currentTask,
              taskId: event.taskId,
              sourceFilePath: filePath ?? state.currentTask.sourceFilePath,
            }
          : state.currentTask;

      if (!filePath) {
        return { currentTask };
      }

      const existing = state.fileTasks[filePath];
      nextTask = {
        ...(existing ?? createRunningTask(filePath)),
        taskId: event.taskId,
        sourceFilePath: filePath,
        status: "running",
      };
      return {
        currentTask,
        fileTasks: {
          ...state.fileTasks,
          [filePath]: nextTask,
        },
        taskFileIndex: { ...state.taskFileIndex, [event.taskId]: filePath },
      };
    });
    if (nextTask) {
      void persistDocumentPublishState(nextTask, {
        refreshTree: true,
        includeReport: true,
      });
    }
  },

  handleProgress: (event) => {
    let nextTask: PublishTaskState | null = null;
    set((state) => {
      const filePath = state.taskFileIndex[event.taskId];
      const currentTask =
        state.currentTask &&
        state.currentTask.status === "running" &&
        (state.currentTask.taskId === event.taskId ||
          (!state.currentTask.taskId &&
            !!filePath &&
            state.currentTask.sourceFilePath === filePath))
          ? {
              ...state.currentTask,
              taskId: event.taskId,
              progress: { message: event.message, percent: event.percent },
            }
          : state.currentTask;

      if (!filePath) {
        return { currentTask };
      }

      const existing = state.fileTasks[filePath];
      nextTask = {
        ...(existing ?? createRunningTask(filePath)),
        taskId: event.taskId,
        sourceFilePath: filePath,
        status: "running",
        progress: { message: event.message, percent: event.percent },
      };
      return {
        currentTask,
        fileTasks: {
          ...state.fileTasks,
          [filePath]: nextTask,
        },
      };
    });
    if (nextTask) {
      void persistDocumentPublishState(nextTask);
    }
  },

  handleCompleted: (event) => {
    clearDocumentPublishPoller(event.taskId);
    let nextTask: PublishTaskState | null = null;
    set((state) => {
      const filePath =
        state.taskFileIndex[event.taskId] ??
        (state.currentTask?.taskId === event.taskId
          ? state.currentTask.sourceFilePath
          : undefined);
      const currentTask =
        state.currentTask &&
        state.currentTask.status === "running" &&
        (state.currentTask.taskId === event.taskId ||
          (!state.currentTask.taskId &&
            !!filePath &&
            state.currentTask.sourceFilePath === filePath))
          ? {
              ...state.currentTask,
              taskId: event.taskId,
              sourceFilePath: filePath ?? state.currentTask.sourceFilePath,
              status: "completed" as const,
              result: event.result,
              progress: { message: tr("publish.progress.completed"), percent: 100 },
            }
          : state.currentTask;

      const fileTasks = filePath
        ? {
            ...state.fileTasks,
            [filePath]: (nextTask = {
              ...(state.fileTasks[filePath] ?? createRunningTask(filePath)),
              taskId: event.taskId,
              sourceFilePath: filePath,
              status: "completed" as const,
              result: event.result,
              progress: { message: tr("publish.progress.completed"), percent: 100 },
            }),
          }
        : state.fileTasks;

      return {
        currentTask,
        fileTasks,
        history: [event.result, ...state.history].slice(0, 20),
      };
    });
    if (nextTask) {
      void persistDocumentPublishState(nextTask, {
        refreshTree: true,
        includeReport: true,
      });
    }
    void refreshPublishedOutput(event.result.outputPath);
  },

  handleFailed: (event) => {
    clearDocumentPublishPoller(event.taskId);
    let nextTask: PublishTaskState | null = null;
    set((state) => {
      const filePath =
        state.taskFileIndex[event.taskId] ??
        (state.currentTask?.taskId === event.taskId
          ? state.currentTask.sourceFilePath
          : undefined);
      const currentTask =
        state.currentTask &&
        state.currentTask.status === "running" &&
        (state.currentTask.taskId === event.taskId ||
          (!state.currentTask.taskId &&
            !!filePath &&
            state.currentTask.sourceFilePath === filePath))
          ? {
              ...state.currentTask,
              taskId: event.taskId,
              sourceFilePath: filePath ?? state.currentTask.sourceFilePath,
              status: "failed" as const,
              error: event.error,
            }
          : state.currentTask;

      const fileTasks = filePath
        ? {
            ...state.fileTasks,
            [filePath]: (nextTask = {
              ...(state.fileTasks[filePath] ?? createRunningTask(filePath)),
              taskId: event.taskId,
              sourceFilePath: filePath,
              status: "failed" as const,
              error: event.error,
            }),
          }
        : state.fileTasks;

      return { currentTask, fileTasks };
    });
    if (nextTask) {
      void persistDocumentPublishState(nextTask, {
        refreshTree: true,
        includeReport: true,
      });
    }
  },

  clearFileTask: (filePath) => {
    set((state) => {
      const task = state.fileTasks[filePath];
      const fileTasks = { ...state.fileTasks };
      delete fileTasks[filePath];

      let taskFileIndex = state.taskFileIndex;
      if (task?.taskId) {
        taskFileIndex = { ...state.taskFileIndex };
        delete taskFileIndex[task.taskId];
      }

      return {
        fileTasks,
        taskFileIndex,
        currentTask:
          state.currentTask?.sourceFilePath === filePath
            ? null
            : state.currentTask,
      };
    });
  },

  clearTask: () => set({ currentTask: null }),

  // ── Site-level actions ──

  startSitePublish: async (params) => {
    // Ensure engine + notification bus are initialized so we receive events
    await usePluginExecutionStore.getState()._initEngine();

    // Set siteTask to running BEFORE the RPC call so notification
    // handlers can accept events even if they arrive before the IPC response.
    set({
      siteTask: {
        taskId: "",
        status: "running",
        progress: { message: tr("publish.progress.startingSite"), percent: 0 },
      },
    });
    publishLog("info", tr("publish.log.siteStart", { path: params.workspacePath }));

    const engine = getPlatformService().engine;
    try {
      const result = await engine.publishSiteStart(params);
      // Update with the real taskId from the server
      set((state) => ({
        siteTask: state.siteTask
          ? { ...state.siteTask, taskId: result.taskId }
          : null,
      }));
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      publishLog("error", tr("publish.log.siteStartFailed", { message: errMsg }));
      set({
        siteTask: {
          taskId: "",
          status: "failed",
          error: errMsg,
        },
      });
    }
  },

  cancelSitePublish: async () => {
    const { siteTask } = get();
    if (!siteTask || siteTask.status !== "running") return;

    const engine = getPlatformService().engine;
    await engine.publishSiteCancel({ taskId: siteTask.taskId });
    publishLog("warn", tr("publish.log.siteCancelled"));
    set({
      siteTask: {
        ...siteTask,
        status: "cancelled",
        progress: { message: tr("publish.progress.cancelled"), percent: 0 },
      },
    });
  },

  handleSiteProgress: (event) => {
    const { siteTask, taskFolderIndex, folderTasks } = get();
    // Exact taskId match (RPC response already received)
    const folderPath =
      taskFolderIndex[event.taskId] ??
      // Fallback: find running folder task with empty taskId (RPC not yet returned)
      Object.keys(folderTasks).find(
        (fp) => folderTasks[fp]?.status === "running" && !folderTasks[fp].taskId,
      );
    if (folderPath && folderTasks[folderPath]?.status === "running") {
      const ft = folderTasks[folderPath];
      set((state) => ({
        folderTasks: {
          ...state.folderTasks,
          [folderPath]: {
            ...ft,
            taskId: event.taskId,
            progress: { message: event.message, percent: event.percent },
            currentPage: event.currentPage,
            pagesProcessed: event.pagesProcessed,
            pagesTotal: event.pagesTotal,
          },
        },
        taskFolderIndex: ft.taskId
          ? state.taskFolderIndex
          : { ...state.taskFolderIndex, [event.taskId]: folderPath },
      }));
      return;
    }
    // Accept events when siteTask is running, matching by taskId or when
    // taskId hasn't been set yet (RPC response not received).
    if (!siteTask || siteTask.status !== "running") return;
    if (siteTask.taskId && siteTask.taskId !== event.taskId) return;
    set({
      siteTask: {
        ...siteTask,
        taskId: event.taskId, // adopt taskId from event if empty
        progress: { message: event.message, percent: event.percent },
        currentPage: event.currentPage,
        pagesProcessed: event.pagesProcessed,
        pagesTotal: event.pagesTotal,
      },
    });
  },

  handleSiteCompleted: (event) => {
    const { siteTask, taskFolderIndex, folderTasks } = get();
    // Exact taskId match (RPC response already received)
    const folderPath =
      taskFolderIndex[event.taskId] ??
      // Fallback: find running folder task with empty taskId (RPC not yet returned)
      Object.keys(folderTasks).find(
        (fp) => folderTasks[fp]?.status === "running" && !folderTasks[fp].taskId,
      );
    if (folderPath && folderTasks[folderPath]?.status === "running") {
      const ft = folderTasks[folderPath];
      const result = event.result;
      publishLog(
        "info",
        tr("publish.log.folderCompleted", {
          path: folderPath,
          built: result.builtPages ?? 0,
          cached: result.cachedPages ?? 0,
          failed: result.failedPages ?? 0,
        }),
      );
      set((state) => ({
        folderTasks: {
          ...state.folderTasks,
          [folderPath]: {
            ...ft,
            taskId: event.taskId,
            status: "completed",
            result,
            progress: { message: tr("publish.progress.folderCompleted"), percent: 100 },
          },
        },
        taskFolderIndex: ft.taskId
          ? state.taskFolderIndex
          : { ...state.taskFolderIndex, [event.taskId]: folderPath },
      }));
      void persistFolderPublishState(folderPath, {
        ...ft,
        taskId: event.taskId,
        status: "completed",
        result,
        progress: { message: tr("publish.progress.folderCompleted"), percent: 100 },
      }, { refreshTree: true, includeReport: true });
      return;
    }
    if (!siteTask || siteTask.status !== "running") return;
    if (siteTask.taskId && siteTask.taskId !== event.taskId) return;
    const result = event.result;
    publishLog(
      "info",
      tr("publish.log.siteCompleted", {
        built: result.builtPages ?? 0,
        cached: result.cachedPages ?? 0,
        failed: result.failedPages ?? 0,
      }),
    );
    set({
      siteTask: {
        ...siteTask,
        taskId: event.taskId,
        status: "completed",
        result,
        progress: { message: tr("publish.progress.siteCompleted"), percent: 100 },
      },
    });
  },

  handleSiteFailed: (event) => {
    const { siteTask, taskFolderIndex, folderTasks } = get();
    // Exact taskId match (RPC response already received)
    const folderPath =
      taskFolderIndex[event.taskId] ??
      // Fallback: find running folder task with empty taskId (RPC not yet returned)
      Object.keys(folderTasks).find(
        (fp) => folderTasks[fp]?.status === "running" && !folderTasks[fp].taskId,
      );
    if (folderPath && folderTasks[folderPath]?.status === "running") {
      const ft = folderTasks[folderPath];
      publishLog("error", tr("publish.log.folderFailed", { path: folderPath, message: event.error }));
      set((state) => ({
        folderTasks: {
          ...state.folderTasks,
          [folderPath]: {
            ...ft,
            taskId: event.taskId,
            status: "failed",
            error: event.error,
          },
        },
        taskFolderIndex: ft.taskId
          ? state.taskFolderIndex
          : { ...state.taskFolderIndex, [event.taskId]: folderPath },
      }));
      void persistFolderPublishState(folderPath, {
        ...ft,
        taskId: event.taskId,
        status: "failed",
        error: event.error,
      }, { refreshTree: true, includeReport: true });
      return;
    }
    if (!siteTask || siteTask.status !== "running") return;
    if (siteTask.taskId && siteTask.taskId !== event.taskId) return;
    publishLog("error", tr("publish.log.siteFailed", { message: event.error }));
    set({
      siteTask: {
        ...siteTask,
        taskId: event.taskId,
        status: "failed",
        error: event.error,
      },
    });
  },

  clearSiteTask: () => set({ siteTask: null }),

  // ── Folder-level publish actions ──

  saveFolderPublishConfig: async (folderPath, options) => {
    await persistFolderPublishConfigFile(folderPath, options);
  },

  publishFolder: async (folderPath, options) => {
    const editor = useEditorStore.getState();
    const existingTask = get().folderTasks[folderPath];
    if (existingTask?.status === "running") {
      console.warn("[publish-store] Folder already publishing:", folderPath);
      editor.setStatusMessage(tr("publish.status.folderRunning"));
      return;
    }

    // Ensure engine + notification bus are initialized
    await usePluginExecutionStore.getState()._initEngine();

    const resolvedOptions = normalizeFolderPublishOptions(folderPath, options);
    const { presentationProfileId, presentationStyleVariables } =
      getCurrentPublishPresentation();

    // Save config first
    await persistFolderPublishConfigFile(folderPath, resolvedOptions);

    const runningTask: SitePublishTaskState = {
      taskId: "",
      status: "running",
      progress: { message: tr("publish.progress.startingFolder"), percent: 0 },
    };

    // Optimistically set running state
    set((state) => ({
      folderTasks: {
        ...state.folderTasks,
        [folderPath]: runningTask,
      },
    }));
    void persistFolderPublishState(folderPath, runningTask, {
      refreshTree: true,
      includeReport: true,
    });

    const engine = getPlatformService().engine;
    try {
      const result = await engine.publishSiteStart({
        workspacePath: folderPath,
        outDir: resolvedOptions.outDir,
        clean: resolvedOptions.clean,
        rerunAll: resolvedOptions.rerunAll,
        incremental: resolvedOptions.incremental,
        siteTitle: resolvedOptions.siteTitle,
        presentationProfileId,
        presentationStyleVariables,
      });

      // Update with real taskId
      set((state) => {
        const ft = state.folderTasks[folderPath];
        if (!ft || ft.status !== "running") return {};
        const updated: SitePublishTaskState = {
          ...ft,
          taskId: result.taskId,
        };
        return {
          folderTasks: {
            ...state.folderTasks,
            [folderPath]: updated,
          },
          taskFolderIndex: {
            ...state.taskFolderIndex,
            [result.taskId]: folderPath,
          },
        };
      });
      void persistFolderPublishState(folderPath, {
        ...runningTask,
        taskId: result.taskId,
      }, { refreshTree: true, includeReport: true });

      publishLog("info", tr("publish.log.folderStart", { path: folderPath }));
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      publishLog("error", tr("publish.log.folderStartFailed", { message: errMsg }));
      const failedTask: SitePublishTaskState = {
        taskId: "",
        status: "failed",
        error: errMsg,
      };
      set((state) => ({
        folderTasks: {
          ...state.folderTasks,
          [folderPath]: failedTask,
        },
      }));
      void persistFolderPublishState(folderPath, failedTask, {
        refreshTree: true,
        includeReport: true,
      });
    }
  },

  cancelFolderPublish: async (folderPath) => {
    const { folderTasks } = get();
    const task = folderTasks[folderPath];
    if (!task || task.status !== "running") return;

    const engine = getPlatformService().engine;
    await engine.publishSiteCancel({ taskId: task.taskId });
    publishLog("warn", tr("publish.log.folderCancelled", { path: folderPath }));
    set((state) => ({
      folderTasks: {
        ...state.folderTasks,
        [folderPath]: {
          ...task,
          status: "cancelled",
          progress: { message: tr("publish.progress.cancelled"), percent: 0 },
        },
      },
    }));
    void persistFolderPublishState(folderPath, {
      ...task,
      status: "cancelled",
      progress: { message: tr("publish.progress.cancelled"), percent: 0 },
    }, { refreshTree: true, includeReport: true });
  },

  clearFolderTask: (folderPath) => {
    set((state) => {
      const task = state.folderTasks[folderPath];
      if (!task) return {};
      const { [folderPath]: _, ...rest } = state.folderTasks;
      const nextIndex = { ...state.taskFolderIndex };
      if (task.taskId) {
        delete nextIndex[task.taskId];
      }
      return { folderTasks: rest, taskFolderIndex: nextIndex };
    });
  },

  // ── Site server actions ──

  startSiteServer: async (outputDir, port = 0) => {
    // Ensure engine + sidecar are available
    await usePluginExecutionStore.getState()._initEngine();
    set({
      siteServer: {
        starting: true,
        running: false,
        url: "",
        port,
        host: "127.0.0.1",
        outputDir,
      },
    });
    publishLog("info", tr("publish.log.siteServerStart", {
      path: outputDir,
      port: port || tr("publish.log.randomPort"),
    }));

    try {
      const engine = getPlatformService().engine;
      const result = await engine.sidecarCall("http", "http/static-serve", {
        path: outputDir,
        host: "127.0.0.1",
        port,
        indexFile: "index.html",
      });

      if (result.success && result.result) {
        const data = result.result as {
          status: string;
          port: number;
          host: string;
          baseUrl: string;
        };
        publishLog("info", tr("publish.log.siteServerStarted", { url: data.baseUrl }));
        set({
          siteServer: {
            starting: false,
            running: true,
            url: data.baseUrl,
            port: data.port,
            host: data.host,
            outputDir,
          },
        });
      } else {
        publishLog(
          "error",
          tr("publish.log.siteServerStartFailed", {
            message: result.error ?? tr("common.unknown"),
          }),
        );
        set({ siteServer: null });
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      publishLog("error", tr("publish.log.siteServerStartFailed", { message: errMsg }));
      set({ siteServer: null });
    }
  },

  stopSiteServer: async () => {
    const { siteServer } = get();
    if (!siteServer?.running) return;

    try {
      const engine = getPlatformService().engine;
      const result = await engine.sidecarCall("http", "http/static-stop", {});
      if (result.success) {
        publishLog("info", tr("publish.log.siteServerStopped"));
      }
    } catch {
      // Ignore stop errors — server may already be gone
    }
    set({ siteServer: null });
  },
}));
